import { SxProps, Theme } from '@mui/material';

/**
 * Typography for the rendered manual. Kept out of the component so the
 * component stays readable: this is the bulk of the styling and none of it is
 * conditional.
 *
 * Colours and spacing come from the theme rather than literals, so the manual
 * follows the app's light and dark modes with the rest of the UI.
 */
export const markdownSx: SxProps<Theme> = {
  p: 3,
  position: 'relative',
  // Cap the measure. The drawer grows wide on a large monitor, and with the
  // sidebar collapsed the text would otherwise run past 140 characters a line,
  // which is hard to read. Printing ignores this.
  maxWidth: 820,
  mx: 'auto',
  '@media print': {
    p: 0,
    pt: 0,
    maxWidth: 'none',
    '& h1': {
      pageBreakAfter: 'avoid',
    },
    '& h2, & h3, & h4': {
      pageBreakAfter: 'avoid',
    },
    '& img': {
      pageBreakInside: 'avoid',
      maxWidth: '100%',
    },
    '& table': {
      pageBreakInside: 'avoid',
    },
    '& pre': {
      pageBreakInside: 'avoid',
    },
  },
  '& h1': {
    fontSize: '2rem',
    fontWeight: 600,
    mb: 2,
    mt: 0,
    color: 'primary.main',
    borderBottom: '2px solid',
    borderColor: 'primary.main',
    pb: 1,
    '&:not(:first-of-type)': {
      mt: 3,
    },
  },
  '& h2': {
    fontSize: '1.5rem',
    fontWeight: 600,
    mb: 2,
    mt: 3,
    color: 'text.primary',
  },
  '& h3': {
    fontSize: '1.25rem',
    fontWeight: 600,
    mb: 1.5,
    mt: 2.5,
    color: 'text.primary',
  },
  '& h4': {
    fontSize: '1.1rem',
    fontWeight: 600,
    mb: 1,
    mt: 2,
    color: 'text.primary',
  },
  '& p': {
    mb: 2,
    lineHeight: 1.7,
    color: 'text.primary',
  },
  '& ul, & ol': {
    mb: 2,
    pl: 3,
    '& li': {
      mb: 0.5,
      lineHeight: 1.6,
    },
  },
  '& code': {
    backgroundColor: 'action.hover',
    px: 0.5,
    py: 0.25,
    borderRadius: 0.5,
    fontSize: '0.875rem',
    fontFamily: 'monospace',
  },
  '& pre': {
    mb: 2,
    borderRadius: 1,
    overflow: 'auto',
  },
  '& blockquote': {
    borderLeft: '4px solid',
    borderColor: 'primary.main',
    pl: 2,
    py: 1,
    my: 2,
    backgroundColor: 'action.hover',
    fontStyle: 'italic',
    '& p': {
      mb: 0,
    },
  },
  '& table': {
    width: '100%',
    mb: 2,
    borderCollapse: 'collapse',
    '& th': {
      backgroundColor: 'primary.main',
      color: 'primary.contrastText',
      p: 1.5,
      textAlign: 'left',
      fontWeight: 600,
    },
    '& td': {
      p: 1.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
    },
    '& tr:hover': {
      backgroundColor: 'action.hover',
    },
  },
  '& a': {
    color: 'primary.main',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 1,
    my: 2,
  },
  '& hr': {
    my: 3,
    borderColor: 'divider',
  },
};
